import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { EntityPin } from "@mapmycustomers/shared/types/map";
import { convertLongLatToLatLngLiteral } from "@mapmycustomers/shared/util/geo/GeoService";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import FeaturedMap, { Feature } from "@app/component/map/FeaturedMap";
import TextWithInfo from "@app/component/typography/TextWithInfo";
import useCalendarView from "@app/component/view/CalendarView/utils/useCalendarView";
import CalendarViewMode from "@app/enum/CalendarViewMode";
import { smartPlannerViewAnalytics } from "@app/scene/activity/activityTableAnalytics";
import RouteBuilder from "@app/scene/activity/component/ActivityCalendarPage/components/Sidebar/RouteBuilder";
import {
  getCalendarMapEntities,
  isCalendarViewLoading,
} from "@app/scene/activity/component/ActivityCalendarPage/store";
import MapEntityWithActivity from "@app/scene/activity/component/ActivityCalendarPage/type/MapEntityWithActivity";
import getActivityDateRange from "@app/scene/activity/utils/getActivityDateRange";
import { getMapSettings, isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { formatDate } from "@app/util/formatters";
import defaultMapEntityGeometryGetter from "@app/util/map/defaultMapEntityGeometryGetter";
import defaultEntityStyleGetter from "@app/util/map/markerStyles/defaultEntityStyleGetter";
import { parseApiDate } from "@app/util/parsers";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import styles from "./Map.module.scss";

const messages = defineMessages({
  [CalendarViewMode.DAY]: {
    id: "activities.calendarView.map.title.day",
    defaultMessage: "Activities this day",
    description: "Sidebar map title when view mode is Day in Activities Calendar view",
  },
  [CalendarViewMode.MONTH]: {
    id: "activities.calendarView.map.title.month",
    defaultMessage: "Activities this month",
    description: "Sidebar map title when view mode is Month in Activities Calendar view",
  },
  [CalendarViewMode.WEEK]: {
    id: "activities.calendarView.map.title.week",
    defaultMessage: "Activities this week",
    description: "Sidebar map title when view mode is Week in Activities Calendar view",
  },
  weekInfo: {
    id: "activities.calendarView.map.title.week.info",
    defaultMessage: "Including weekends",
    description: "Sidebar map tooltip when view mode is Week in Activities Calendar view",
  },
});

const isDifferent = (a: MapEntityWithActivity, b: MapEntityWithActivity) =>
  a.entity.id !== b.entity.id && a.entity.entity !== b.entity.entity;

const idGetter = (entity: MapEntityWithActivity) => entity.entity.id;

interface Props {
  className?: string;
  isReadOnlyMember: boolean;
  mapEntities: MapEntityWithActivity[];
  mapSettings: {
    markerSize: MarkerSize;
    showEntityIcons: boolean;
  };
}

const Map: React.FC<Props> = ({ className, isReadOnlyMember, mapEntities, mapSettings }) => {
  const intl = useIntl();
  const [map, setMap] = useState<google.maps.Map | undefined>();
  const {
    viewState: { filter, viewMode },
  } = useCalendarView();

  const rangeText = useMemo(() => {
    if (filter.startAt && isSimpleCondition(filter.startAt)) {
      const [start, end] = getActivityDateRange(parseApiDate(filter.startAt.value), viewMode);
      return `${formatDate(start, "MMM d")}${
        viewMode === CalendarViewMode.DAY
          ? ""
          : ` - ${formatDate(
              end,
              `${formatDate(start, "M") === formatDate(end, "M") ? "" : "MMM "}d`
            )}`
      }`;
    }
    return "";
  }, [filter, viewMode]);

  const handlePreviewClick = useCallback(({ activity }: MapEntityWithActivity) => {
    const element = document.querySelector(`.act_${activity.id}.fc-event`);
    if (element && element instanceof HTMLElement) {
      element.scrollIntoView({ block: "center" });
      setTimeout(() => element.click(), 0);
    }
  }, []);

  useEffect(() => {
    if (map) {
      const bounds = new google.maps.LatLngBounds();
      mapEntities.forEach((item: MapEntityWithActivity) => {
        const geoPoint =
          item.entity.entity === EntityType.DEAL
            ? item.entity.account?.geoPoint ?? item.entity.contact?.geoPoint
            : item.entity.geoPoint;
        bounds.extend(convertLongLatToLatLngLiteral(geoPoint!.coordinates));
      });
      map.fitBounds(bounds);
    }
  }, [map, mapEntities]);

  const [routeBuilderVisible, showRouteBuilder, hideRouteBuilder] = useBoolean();

  const mapEntityWithActivityGeometryGetter = useCallback(
    (entity: MapEntityWithActivity) => defaultMapEntityGeometryGetter(entity.entity),
    []
  );

  const entityPinStyleGetter = useMemo(() => {
    return (entity: MapEntityWithActivity) =>
      defaultEntityStyleGetter({
        draggable: false,
        icon: mapSettings.showEntityIcons
          ? (entityPin: EntityPin) => entityPin.data.entity
          : undefined,
        markerSize: mapSettings.markerSize,
        shape: (entityPin: EntityPin) => entityPin.data.shape ?? MarkerShape.SQUARE,
        showEntityIcon: mapSettings.showEntityIcons,
      })({
        id: entity.entity.id,
        count: 1,
        data: entity.entity,
        multiPin: false,
      } as unknown as EntityPin); // some dirty conversions here because defaultEntityStyleGetter only works with EntityPins
  }, [mapSettings]);

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.title}>
        <TextWithInfo
          info={
            viewMode === CalendarViewMode.WEEK ? intl.formatMessage(messages.weekInfo) : undefined
          }
        >
          {intl.formatMessage(messages[viewMode])}
        </TextWithInfo>
      </div>
      <div className={styles.rangeText}>{rangeText}</div>
      <div className={styles.content}>
        {!isReadOnlyMember && (
          <div className={styles.buildRoutes}>
            <span>
              {intl.formatMessage({
                id: "activities.calendarView.map.buildRoute.title",
                defaultMessage: "Build route from activities",
                description: "Build route label in Activities Calendar view",
              })}
            </span>
            <Button
              onClick={() => {
                smartPlannerViewAnalytics?.initiated(["Build routes from activities"]);
                showRouteBuilder();
              }}
              size="small"
              type="primary"
            >
              {intl.formatMessage({
                id: "activities.calendarView.map.buildRoutes.button",
                defaultMessage: "Go",
                description: "Build routes button in Activities Calendar view",
              })}
            </Button>
          </div>
        )}
        <div className={styles.map}>
          <FeaturedMap
            className={styles.mapContainer}
            initialOptions={{
              controlSize: 32,
            }}
            mapClassName={styles.map}
            onInitialized={setMap}
          >
            <Feature<MapEntityWithActivity>
              geometryGetter={mapEntityWithActivityGeometryGetter}
              idGetter={idGetter}
              isDifferent={isDifferent}
              items={mapEntities}
              onClick={handlePreviewClick}
              styleGetter={entityPinStyleGetter}
              type="preview-pin"
            />
          </FeaturedMap>
        </div>
      </div>
      {routeBuilderVisible && <RouteBuilder onHide={hideRouteBuilder} />}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isReadOnlyMember: isCurrentUserReadOnly(state),
  loading: isCalendarViewLoading(state),
  mapEntities: getCalendarMapEntities(state),
  mapSettings: getMapSettings(state),
});

export default connect(mapStateToProps)(Map);
